import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Brain, AlertTriangle, ClipboardCheck, ExternalLink, Sparkles } from "lucide-react";
import {
  resolvePattern, findChain, findTest, PATTERNS,
} from "@/data/mioMatrix";

/**
 * ClinicalReasoningPanel — raciocínio clínico contextual.
 *
 * Lê `analysis.patterns`, `analysis.chains`, `analysis.consequences` já
 * calculados pelo backend e cruza com a MIO Matrix Library (data/mioMatrix.js)
 * para sugerir cadeias relacionadas, testes e hipóteses funcionais.
 *
 * Renderiza SEMPRE (mesmo em casos leves) com densidade adaptativa:
 *   - discreto:    1 cadeia · 1-2 testes · linguagem mais conservadora
 *   - moderado:    2-3 cadeias · testes complementares
 *   - expressivo:  cadeias + hipóteses + testes (modo completo)
 *
 * Linguagem rigorosamente sugestiva e não-diagnóstica:
 *   - "Pode sugerir…" / "Padrão funcional discreto compatível com…"
 *   - "Recomenda-se validação manual pelo osteopata"
 *
 * NÃO faz inferências automáticas, NÃO altera scoring, NÃO chama LLM, NÃO
 * altera backend. Pure derived view.
 */

// Heurística leve para escolher cadeia dominante quando o backend não
// devolve patterns. Usa observações textuais e desvios da linha de
// gravidade já calculados — sem cálculos novos, sem inferência clínica.
function deriveFallbackChainId(analysis) {
  const findings = (analysis?.segments?.spine?.findings || []).join(" ").toLowerCase();
  const dev = analysis?.plumb_line?.deviations || [];

  // Sinais textuais explícitos têm prioridade.
  if (/\banterior|anterioriza|cifose anterior|anteriorizad/.test(findings)) return "lsa";
  if (/\bposterior|hiperextens|posterioriza/.test(findings)) return "lsp";
  if (/\blordose|hiperlordose|antevers/.test(findings)) return "ldp";
  if (/\blateral|inclina|escolio/.test(findings)) return "ll";

  // Caso contrário, olha para o ponto com maior |dx| significativo.
  const sig = dev
    .filter(d => d?.status && d.status !== "alinhado" && typeof d.dx === "number")
    .sort((a, b) => Math.abs(b.dx) - Math.abs(a.dx))[0];
  if (sig) {
    if (sig.point === "ear" || sig.point === "shoulder") return "lsa";
    if (sig.point === "hip" || sig.point === "knee")     return "ldp";
  }
  // Default postural: linha superficial posterior (eixo gravitacional global).
  return "lsp";
}

function computeTier(analysis, matchedCount) {
  const score = analysis?.global_score;
  if (matchedCount >= 3) return "expressivo";
  if (matchedCount >= 1) return "moderado";
  if (typeof score === "number") {
    if (score < 75) return "expressivo";
    if (score < 88) return "moderado";
  }
  return "discreto";
}

const TIER_META = {
  discreto: {
    headline: "Sugestões discretas — perfil estrutural alinhado",
    subhead: "Padrão funcional discreto compatível com adaptação biomecânica suave. Recomenda-se validação manual pelo osteopata.",
    hypothesisLead: "Padrão funcional discreto compatível com",
    accent: "emerald",
  },
  moderado: {
    headline: "Raciocínio clínico · MIO Matrix",
    subhead: "Sugestões cruzadas com os achados detetados. Recomenda-se validação manual pelo osteopata.",
    hypothesisLead: "Pode sugerir",
    accent: "sky",
  },
  expressivo: {
    headline: "Raciocínio clínico · MIO Matrix",
    subhead: "Sugestões cruzadas com os achados detetados. Recomenda-se validação manual pelo osteopata.",
    hypothesisLead: "Pode sugerir",
    accent: "violet",
  },
};

// Antes de navegar para a Matrix, guarda contexto para retorno suave.
function saveMatrixBackContext(assessmentId) {
  try {
    sessionStorage.setItem("mio:matrix:back", JSON.stringify({
      from: "workspace",
      assessmentId: assessmentId || null,
      scrollY: window.scrollY || window.pageYOffset || 0,
      ts: Date.now(),
    }));
  } catch { /* sessionStorage indisponível — fallback gracioso */ }
}

function buildMatrixLink(section, id, assessmentId) {
  const params = new URLSearchParams({ section, id });
  if (assessmentId) {
    params.set("from", "workspace");
    params.set("aid", assessmentId);
  }
  return `/library?${params.toString()}`;
}

export default function ClinicalReasoningPanel({ analysis, assessmentId }) {
  const enriched = useMemo(() => {
    if (!analysis) return null;
    const seen = new Set();
    const matched = [];

    for (const p of analysis.patterns || []) {
      const m = resolvePattern(p);
      if (m && !seen.has(m.id)) {
        matched.push({
          ...m,
          backendName: p?.name || (typeof p === "string" ? p : ""),
          backendDescription: p?.description || "",
        });
        seen.add(m.id);
      }
    }

    const chainHits = new Set();
    for (const c of analysis.chains || []) {
      const name = (c?.name || c || "").toLowerCase();
      for (const ch of [
        { id: "lsp", needle: "posterior" }, { id: "lsa", needle: "anterior" },
        { id: "ll", needle: "lateral" }, { id: "le", needle: "espiral" },
        { id: "lf", needle: "funcional" }, { id: "ldp", needle: "profund" },
        { id: "ascendente", needle: "ascendente" }, { id: "descendente", needle: "descendente" },
      ]) {
        if (name.includes(ch.needle)) chainHits.add(ch.id);
      }
    }
    for (const p of matched) (p.chains || []).forEach(id => chainHits.add(id));
    let chains = [...chainHits].map(findChain).filter(Boolean);

    const testHits = new Set();
    for (const p of matched) (p.tests || []).forEach(id => testHits.add(id));
    for (const c of chains) (c.tests || []).forEach(id => testHits.add(id));
    let tests = [...testHits].map(findTest).filter(Boolean);

    const tier = computeTier(analysis, matched.length);

    // Fallback: garante sempre 1 cadeia + 1-2 testes mesmo em casos leves.
    let usedFallback = false;
    if (chains.length === 0) {
      const fallbackId = deriveFallbackChainId(analysis);
      const fb = findChain(fallbackId);
      if (fb) {
        chains = [fb];
        usedFallback = true;
      }
    }
    if (tests.length === 0 && chains.length > 0) {
      const ids = chains[0].tests || [];
      tests = ids.map(findTest).filter(Boolean).slice(0, 2);
    }

    // Densidade por tier (limita o que se mostra em modo discreto).
    if (tier === "discreto") {
      chains = chains.slice(0, 1);
      tests = tests.slice(0, 2);
    } else if (tier === "moderado") {
      chains = chains.slice(0, 3);
      tests = tests.slice(0, 6);
    }

    return { matched, chains, tests, tier, usedFallback };
  }, [analysis]);

  if (!enriched) return null;
  const { matched, chains, tests, tier, usedFallback } = enriched;
  const meta = TIER_META[tier];

  return (
    <div
      data-testid="clinical-reasoning-panel"
      data-tier={tier}
      className="rounded-2xl border border-sky-500/20 bg-gradient-to-br from-slate-950/60 to-sky-950/20 p-6"
    >
      <div className="flex items-center gap-2">
        <Brain className="w-4 h-4 text-sky-300" strokeWidth={1.5} />
        <div className="text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">
          {meta.headline}
        </div>
        <span
          data-testid="reasoning-tier-badge"
          className={`ml-1 text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-[0.18em] ${
            tier === "expressivo" ? "border-violet-500/40 text-violet-200 bg-violet-500/10"
            : tier === "moderado" ? "border-sky-500/40 text-sky-200 bg-sky-500/10"
            : "border-emerald-500/30 text-emerald-200 bg-emerald-500/5"
          }`}
        >
          {tier}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mt-1 italic">{meta.subhead}</p>

      {/* Hipóteses — só aparecem com matches reais (não inventamos). */}
      {matched.length > 0 && (
        <div className="mt-4 space-y-3" data-testid="reasoning-hypotheses">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-mono">Hipóteses funcionais sugeridas</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matched.map(p => (
              <div key={p.id} data-testid={`reasoning-pattern-${p.id}`} className="rounded-xl border border-violet-500/25 bg-violet-500/5 p-4 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-slate-50 font-medium">{meta.hypothesisLead}: {p.title}</div>
                  <Link
                    to={buildMatrixLink("patterns", p.id, assessmentId)}
                    onClick={() => saveMatrixBackContext(assessmentId)}
                    data-testid={`reasoning-pattern-link-${p.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-mono text-violet-300/80 hover:text-violet-200 transition"
                    title="Ver no Matrix"
                  >
                    <ExternalLink className="w-3 h-3" /> Ver no Matrix
                  </Link>
                </div>
                {p.description && <div className="text-sm text-slate-300">{p.description}</div>}
                {p.regions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {p.regions.slice(0, 5).map((r, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-violet-500/30 text-violet-200 bg-violet-500/5">{r}</span>
                    ))}
                  </div>
                )}
                {p.approach && (
                  <div className="text-[12px] text-slate-300 italic pt-1">Abordagem possível: {p.approach}</div>
                )}
                <div className="text-[10px] text-amber-300/80 pt-1 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Recomenda-se validação manual pelo osteopata.
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cadeias miofasciais relacionadas — sempre presentes. */}
      {chains.length > 0 && (
        <div className="mt-5" data-testid="reasoning-chains">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-sky-300/80" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-mono">Cadeias miofasciais relacionadas</div>
            {usedFallback && (
              <span className="text-[9px] text-slate-500 italic">· sugestão exploratória</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {chains.map(c => (
              <div key={c.id} data-testid={`reasoning-chain-${c.id}`} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-slate-100 text-sm font-medium">{c.title}</div>
                  <Link
                    to={buildMatrixLink("chains", c.id, assessmentId)}
                    onClick={() => saveMatrixBackContext(assessmentId)}
                    data-testid={`reasoning-chain-link-${c.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-mono text-sky-300/80 hover:text-sky-200 transition"
                    title="Ver no Matrix"
                  >
                    <ExternalLink className="w-3 h-3" /> Matrix
                  </Link>
                </div>
                {c.function && <div className="text-[12px] text-slate-400 mt-0.5">{c.function}</div>}
                {c.compensations?.length > 0 && tier !== "discreto" && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Compensações típicas: <span className="text-slate-300">{c.compensations.slice(0, tier === "expressivo" ? 3 : 2).join(" · ")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Testes sugeridos — sempre presentes (mín. 1 quando há cadeia). */}
      {tests.length > 0 && (
        <div className="mt-5" data-testid="reasoning-tests">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardCheck className="w-3.5 h-3.5 text-emerald-300" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-mono">Testes sugeridos</div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tests.map(t => (
              <Link
                key={t.id}
                to={buildMatrixLink("tests", t.id, assessmentId)}
                onClick={() => saveMatrixBackContext(assessmentId)}
                data-testid={`reasoning-test-${t.id}`}
                title={t.goal || "Ver no Matrix"}
                className="text-[11px] px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:border-emerald-500/50 transition inline-flex items-center gap-1"
              >
                {t.title}{t.category ? ` · ${t.category}` : ""}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </Link>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-2 italic">
            {tier === "discreto"
              ? "Testes complementares opcionais — usar apenas se o quadro o justificar."
              : "Sugestões para complementar a avaliação clínica. Realiza apenas os testes apropriados ao contexto."}
          </div>
        </div>
      )}
    </div>
  );
}

// Re-export para auxiliar testes diretos do módulo se necessário.
export { PATTERNS };
